import { CommonModule, TitleCasePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ToastrService } from 'ngx-toastr';

import {
  Client,
  PermissionsEnum,
  RoleCrud,
  RoleDataIn,
} from '../../../../api/api-reference';

interface PermissionGroup {
  name: string;
  permissions: PermissionsEnum[];
  selectAll: boolean;
}

@Component({
  selector: 'app-role-setup',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    AngularSvgIconModule,
    RouterLink,
    FormsModule,
    TitleCasePipe
  ],
  templateUrl: './role-setup.component.html',
  styleUrl: './role-setup.component.scss',
})
export class RoleSetupComponent implements OnInit {
  roleId: string = '';
  data: RoleCrud = new RoleCrud();
  permissionGroups: PermissionGroup[] = [];

  private allPermissions: PermissionsEnum[] = Object.values(PermissionsEnum);
  private permissionCategories = [
    'device',
    'alarms',
    'ondemand',
    'scheduled',
    'remotecommands',
    'settings',
    'personalize',
    'gsmapi',
    'scheduler',
    'systemmonitoring',
    'report',
    'auditlog',
    'desktop',
    'loadlimit'
  ];

  constructor(
    private toastr: ToastrService,
    private client: Client,
    private router: Router,
  ) { }

  ngOnInit(): void {
    const state = history.state;
    if (state && state.userData) {
      this.roleId = state.userData;
    }
    if (!this.data.permissions) {
      this.data.permissions = [];
    }
    this.initializePermissionGroups();
    if (this.roleId) this.getRole();
  }

  private initializePermissionGroups(): void {
    this.permissionGroups = this.permissionCategories.map(category => ({
      name: category,
      permissions: this.getPermissionsForCategory(category),
      selectAll: false
    }));
  }

  getRole() {
    this.client.getRole(this.roleId).subscribe({
      next: (response) => {
        this.data = response ?? [];
        this.checkAndUpdateSelectAllCheckboxes();
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  checkAndUpdateSelectAllCheckboxes() {
    if (this.data.permissions) {
      this.permissionGroups.forEach(group => {
        const allPresent = group.permissions.every(p =>
          this.data.permissions!.includes(p),
        );
        if (allPresent && group.permissions.length > 0) {
          group.selectAll = true;
        }
      });
    }
  }

  onSubmit() {
    const roleDataIn = Object.assign(new RoleDataIn(), {
      id: this.data.id,
      name: this.data.name,
      permissions: this.data.permissions,
    });

    this.client.saveRole(roleDataIn).subscribe({
      next: (response) => {
        this.toastr.success(response);
        this.router.navigate(['settings/team/roles-permissions']);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  togglePermission(permission: PermissionsEnum, event: Event) {
    const isChecked = (event.target as HTMLInputElement).checked;

    if (isChecked) {
      if (!this.data.permissions!.includes(permission)) {
        this.data.permissions!.push(permission);
      }
    } else {
      this.data.permissions = this.data.permissions!.filter(p => p !== permission);
    }

    this.checkAndUpdateSelectAllCheckboxes();
  }

  toggleSelectAllPermissions(group: PermissionGroup, event: Event) {
    const isChecked = (event.target as HTMLInputElement).checked;
    group.selectAll = isChecked;

    const groupPermissions = group.permissions;

    if (isChecked) {
      const permissionsToAdd = groupPermissions.filter(
        p => !this.data.permissions!.includes(p)
      );
      this.data.permissions!.push(...permissionsToAdd);
    } else {
      this.data.permissions = this.data.permissions!.filter(
        p => !groupPermissions.includes(p)
      );
    }
  }

  private getPermissionsForCategory(category: string): PermissionsEnum[] {
    return this.allPermissions.filter(p => p.startsWith(category + '_'));
  }
}
